import {
  Compass,
  Star,
  BookOpen,
  Flame,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-slate-200/80 [html[data-theme='dark']_&]:bg-white/5 dark:bg-white/5",
        className
      )}
    />
  );
}

export function BlogCardSkeleton({ isLight }: { isLight?: boolean }) {
  return (
    <div
      className={cn(
        "relative flex flex-col h-full rounded-2xl border overflow-hidden transition-all duration-300",
        isLight
          ? "bg-white border-slate-200/90 shadow-sm"
          : "bg-[#0B132B]/90 border-slate-800/90 shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
      )}
    >
      {/* Thumbnail Skeleton */}
      <div className={cn(
        "relative aspect-[16/10] overflow-hidden shrink-0",
        isLight ? "bg-slate-100" : "bg-slate-900"
      )}>
        <Skeleton className="w-full h-full rounded-none" />
        <div className="absolute top-3 left-3 z-10">
          <Skeleton className="h-5 w-20 rounded-md" />
        </div>
      </div>

      {/* Details Container */}
      <div className="p-5 flex-1 flex flex-col justify-between">
        <div>
          {/* Meta: Date & Read Time */}
          <div className="flex items-center justify-between mb-3">
            <Skeleton className="h-3.5 w-24 rounded" />
            <Skeleton className="h-3.5 w-16 rounded" />
          </div>

          {/* Title */}
          <div className="space-y-2 mb-3.5">
            <Skeleton className="h-5 w-full rounded" />
            <Skeleton className="h-5 w-4/5 rounded" />
          </div>

          {/* Excerpt */}
          <div className="space-y-1.5 mb-5">
            <Skeleton className="h-3.5 w-full rounded" />
            <Skeleton className="h-3.5 w-11/12 rounded" />
            <Skeleton className="h-3.5 w-2/3 rounded" />
          </div>
        </div>

        {/* Footer */}
        <div className={cn(
          "pt-3.5 border-t flex justify-between items-center",
          isLight ? "border-slate-100" : "border-white/5"
        )}>
          <Skeleton className="h-4 w-24 rounded" />
          <Skeleton className="h-3.5 w-20 rounded" />
        </div>
      </div>
    </div>
  );
}

export function BlogListingSkeleton({ isLight }: { isLight?: boolean }) {
  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start relative w-full">
      {/* ── MAIN ARTICLES FEED SKELETON (3-COLUMN ON DESKTOP) ── */}
      <div className="flex-1 min-w-0 w-full space-y-6">
        {/* Section Header */}
        <div className={cn(
          "flex items-center justify-between pb-3 border-b",
          isLight ? "border-slate-200" : "border-slate-800"
        )}>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-brand-600 dark:text-brand-400" />
            <span className={cn(
              "text-base sm:text-lg font-display font-bold tracking-tight",
              isLight ? "text-slate-900" : "text-white"
            )}>
              Latest Articles
            </span>
          </div>
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>

        {/* Articles Grid (3-Column on Desktop, 2 on Tablet) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <BlogCardSkeleton key={i} isLight={isLight} />
          ))}
        </div>
      </div>

      {/* ── RIGHT SIDEBAR SKELETON: TRENDING NOW + TECH ADVISORY ── */}
      <aside className="w-full lg:w-[320px] xl:w-[340px] shrink-0 lg:sticky lg:top-28 space-y-6">
        {/* Trending Now Card */}
        <div className={cn(
          "p-4 sm:p-5 rounded-2xl border backdrop-blur-xl transition-all shrink-0",
          isLight
            ? "bg-white/95 border-slate-200/90 shadow-md shadow-slate-200/40"
            : "bg-[#0B132B]/90 border-slate-800 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        )}>
          <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-200 dark:border-white/5">
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="font-display font-bold text-xs tracking-wider uppercase text-orange-500">
              Trending Now
            </span>
          </div>

          <div className="space-y-3.5">
            {[...Array(4)].map((_, idx) => (
              <div
                key={idx}
                className="flex gap-3.5 items-start pb-3.5 border-b border-slate-100 dark:border-white/5 last:border-0 last:pb-0"
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
                  <Skeleton className="w-full h-full rounded-xl" />
                </div>
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-full rounded" />
                  <Skeleton className="h-3.5 w-3/4 rounded" />
                  <div className="flex items-center gap-2 pt-0.5">
                    <Skeleton className="h-2.5 w-12 rounded" />
                    <Skeleton className="h-2.5 w-14 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Advisory Card */}
        <div className={cn(
          "p-4 sm:p-5 rounded-2xl border backdrop-blur-xl transition-all relative overflow-hidden shrink-0",
          isLight
            ? "bg-white/95 border-slate-200/90 shadow-md shadow-slate-200/40"
            : "bg-gradient-to-br from-[#0B132B]/95 to-[#060B18]/95 border-brand-500/20 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        )}>
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className={cn("w-4 h-4", isLight ? "text-brand-600" : "text-brand-400")} />
            <span className={cn(
              "font-display font-bold text-xs tracking-wider uppercase",
              isLight ? "text-slate-900" : "text-white"
            )}>
              Tech Advisory
            </span>
          </div>

          <div className="space-y-2 mb-4">
            <Skeleton className="h-3 w-full rounded" />
            <Skeleton className="h-3 w-5/6 rounded" />
            <Skeleton className="h-3 w-4/6 rounded" />
          </div>

          <div className={cn(
            "pt-3 border-t flex items-center justify-between",
            isLight ? "border-slate-100" : "border-white/10"
          )}>
            <Skeleton className="h-3 w-28 rounded" />
            <Sparkles className="w-3.5 h-3.5 text-brand-400" />
          </div>
        </div>
      </aside>
    </div>
  );
}

export function BlogSkeleton({ isLight }: { isLight?: boolean }) {
  return <BlogCardSkeleton isLight={isLight} />;
}

export function BlogDetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#050505] overflow-hidden">
      <div className="h-[70vh] w-full relative">
        <Skeleton className="h-full w-full rounded-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="absolute bottom-20 left-0 right-0 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-16 w-3/4" />
            <div className="flex gap-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
      </div>
      <div className="py-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_auto] gap-16">
          <div className="space-y-4">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className={cn("h-4", i % 3 === 0 ? "w-full" : i % 3 === 1 ? "w-5/6" : "w-4/5")} />
            ))}
          </div>
          <div className="hidden lg:block w-64 space-y-12">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-60 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
